import { createFileRoute, Link, useParams, Outlet } from '@tanstack/react-router'
import { supabase } from '@/integrations/supabase/client'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Calendar, Clock, ArrowRight, ChevronLeft, ChevronRight, Filter } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute('/guias/$categoria')({
  component: GuiasCategoriaPage,
})

function GuiasCategoriaPage() {
  const { categoria, slug } = useParams({ strict: false }) as { categoria: string; slug?: string }
  const [page, setPage] = useState(0)
  const [periodo, setPeriodo] = useState<'todos' | 'mes' | 'ano'>('todos')
  const itemsPerPage = 12

  const { data: categoriaInfo } = useQuery({
    queryKey: ['categoria-blog', categoria],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categorias_blog')
        .select('*')
        .eq('slug', categoria)
        .single()
      if (error) throw error
      return data
    }
  })

  const { data: countData } = useQuery({
    queryKey: ['artigos-categoria-count', categoria, periodo],
    queryFn: async () => {
      let query = supabase
        .from('artigos')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'publicado')
      
      if (categoriaInfo?.id) {
        query = query.eq('categoria_id', categoriaInfo.id)
      }

      if (periodo === 'mes') {
        const date = new Date()
        date.setMonth(date.getMonth() - 1)
        query = query.gte('publicado_em', date.toISOString())
      } else if (periodo === 'ano') {
        const date = new Date()
        date.setFullYear(date.getFullYear() - 1)
        query = query.gte('publicado_em', date.toISOString())
      }

      const { count, error } = await query
      if (error) throw error
      return count || 0
    },
    enabled: !!categoriaInfo
  })

  const { data: artigos, isLoading } = useQuery({
    queryKey: ['artigos-categoria', categoria, page, periodo],
    queryFn: async () => {
      let query = supabase
        .from('artigos')
        .select('*, categorias_blog(nome, slug)')
        .eq('status', 'publicado')
        .order('publicado_em', { ascending: false })
      
      if (categoriaInfo?.id) {
        query = query.eq('categoria_id', categoriaInfo.id)
      }

      if (periodo === 'mes') {
        const date = new Date()
        date.setMonth(date.getMonth() - 1)
        query = query.gte('publicado_em', date.toISOString())
      } else if (periodo === 'ano') {
        const date = new Date()
        date.setFullYear(date.getFullYear() - 1)
        query = query.gte('publicado_em', date.toISOString())
      }

      const { data, error } = await query
        .range(page * itemsPerPage, (page + 1) * itemsPerPage - 1)
      
      if (error) throw error
      return data
    },
    enabled: !!categoriaInfo
  })

  const totalPages = Math.ceil((countData || 0) / itemsPerPage)

  if (slug) {
    return <Outlet />
  }

  const canonicalUrl = `https://listafornecedores.com.br/guias/${categoria}${page > 0 ? `?page=${page + 1}` : ''}`
  const prevUrl = page > 0 ? `https://listafornecedores.com.br/guias/${categoria}${page === 1 ? '' : `?page=${page}`}` : null
  const nextUrl = page < totalPages - 1 ? `https://listafornecedores.com.br/guias/${categoria}?page=${page + 2}` : null

  return (
    <div className="container mx-auto px-4 py-12">
      <Helmet>
        <title>{categoriaInfo?.nome ? `${categoriaInfo.nome} | Guias Gopage` : 'Guias Gopage'}</title>
        <meta name="description" content={`Explore nossos guias sobre ${categoriaInfo?.nome || 'fornecedores e atacadistas'}.`} />
        <link rel="canonical" href={canonicalUrl} />
        {prevUrl && <link rel="prev" href={prevUrl} />}
        {nextUrl && <link rel="next" href={nextUrl} />}
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Início",
                "item": "https://listafornecedores.com.br"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Guias",
                "item": "https://listafornecedores.com.br/guias"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": categoriaInfo?.nome || 'Categoria',
                "item": `https://listafornecedores.com.br/guias/${categoria}`
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
        <div>
          <nav className="flex items-center gap-2 text-sm text-[#6B7280] mb-4">
            <Link to="/" className="hover:text-[#0C1A12]">Início</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/guias" className="hover:text-[#0C1A12]">Guias</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#0C1A12] font-medium">{categoriaInfo?.nome}</span>
          </nav>
          <h1 className="text-4xl font-bold text-[#0C1A12]">
            {categoriaInfo?.nome || 'Carregando...'}
          </h1>
          <p className="text-[#6B7280] mt-2">
            {countData} {countData === 1 ? 'guia encontrado' : 'guias encontrados'} nesta categoria
          </p>
        </div>

        <div className="flex items-center gap-2 bg-white p-1 rounded-xl border border-[#E5E7EB] shadow-sm">
          <Button 
            variant={periodo === 'todos' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => { setPeriodo('todos'); setPage(0); }}
            className={periodo === 'todos' ? 'bg-[#C6F764] text-[#0C1A12] hover:bg-[#C6F764]/90' : 'text-[#6B7280]'}
          >
            Todos
          </Button>
          <Button 
            variant={periodo === 'mes' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => { setPeriodo('mes'); setPage(0); }}
            className={periodo === 'mes' ? 'bg-[#C6F764] text-[#0C1A12] hover:bg-[#C6F764]/90' : 'text-[#6B7280]'}
          >
            Último Mês
          </Button>
          <Button 
            variant={periodo === 'ano' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => { setPeriodo('ano'); setPage(0); }}
            className={periodo === 'ano' ? 'bg-[#C6F764] text-[#0C1A12] hover:bg-[#C6F764]/90' : 'text-[#6B7280]'}
          >
            Último Ano
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="rounded-2xl border-none shadow-sm overflow-hidden">
              <Skeleton className="h-48 w-full" />
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-8 w-full mb-4" />
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : artigos && artigos.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {artigos.map((artigo) => (
              <Link 
                key={artigo.id} 
                to="/guias/$categoria/$slug" 
                params={{ 
                  categoria: artigo.categorias_blog?.slug || 'geral', 
                  slug: artigo.slug 
                }}
              >
                <Card className="group h-full rounded-2xl border-none shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden bg-white">
                  <div className="aspect-video relative overflow-hidden">
                    {artigo.imagem_capa ? (
                      <img 
                        src={artigo.imagem_capa} 
                        alt={artigo.titulo}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#0C1A12] flex items-center justify-center">
                        <span className="text-white/20 font-bold text-2xl">Gopage</span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6 flex flex-col h-[calc(100%-aspect-video)]">
                    <h3 className="text-xl font-bold text-[#1A1A1A] mb-3 group-hover:text-[#0C1A12] line-clamp-2">
                      {artigo.titulo}
                    </h3>
                    <p className="text-[#6B7280] text-sm mb-6 line-clamp-3 flex-grow">
                      {artigo.resumo}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-[#E5E7EB]">
                      <div className="flex items-center gap-4 text-xs text-[#6B7280]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {artigo.tempo_leitura} min
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {artigo.publicado_em && format(new Date(artigo.publicado_em), 'dd MMM', { locale: ptBR })}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#C6F764] transition-transform group-hover:translate-x-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-12">
              <Button
                variant="outline"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="rounded-xl border-[#E5E7EB]"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
              <span className="text-sm font-medium text-[#6B7280]">
                Página {page + 1} de {totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                className="rounded-xl border-[#E5E7EB]"
              >
                Próxima
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-[#E5E7EB]">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F7F9F5] mb-4">
            <Filter className="w-8 h-8 text-[#6B7280]" />
          </div>
          <h3 className="text-xl font-bold text-[#0C1A12] mb-2">Nenhum guia encontrado</h3>
          <p className="text-[#6B7280]">
            Não encontramos guias para os filtros selecionados neste período.
          </p>
          <Button 
            variant="link" 
            onClick={() => { setPeriodo('todos'); setPage(0); }}
            className="text-[#C6F764] mt-4"
          >
            Limpar filtros
          </Button>
        </div>
      )}
    </div>
  )
}
